import { Button, Field, Modal } from "@deligoo/ui"
import dayjs from "dayjs"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"

import { useAppSelector } from "@/App"
import { selectDriverById, updateDriver } from "@/store/drivers"
import { Driver } from "@/types"
import { fetchSetTemporaryBreak } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import cls from "../DriverContextMenu.module.scss"

type DriversFinalBreakModalProps = {
  driverId: Driver["id"]
  handleClose: () => void
}

export const DriversFinalBreakModal = ({ driverId, handleClose }: DriversFinalBreakModalProps) => {
  const driver = useAppSelector(selectDriverById(driverId))

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [driverIdEndOfWork, setDriverIdEndOfWork] = useState(dayjs().format("HH:mm"))
  const [reason, setReason] = useState("")

  const { t } = useTranslation()

  const dispatch = useDispatch()

  if (!driver) return null

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (!driver) return null

    const endOfWorkTime = dayjs(driverIdEndOfWork, "HH:mm").format()

    setIsSubmitting(true)

    try {
      const { response, json } = await fetchSetTemporaryBreak(driver.id, endOfWorkTime, reason)

      const updatedDriver = json?.data

      if (response.ok && updatedDriver) {
        toast.success(t("drivers:end_of_shift_success"))
        dispatch(updateDriver(updatedDriver))
        handleClose()
      } else {
        handleStandardError(response, json)
      }
    } catch (error) {
      toast.error(t("drivers:end_of_shift_fail"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal handleClose={handleClose} className={cls.driversBreakModal} closeBtnPosition="inside">
      <Modal.Title>
        {t("drivers:courier")}: {driver.name}
        <div className="h300 mt-4">{t("drivers:add_end_of_shift")}</div>
      </Modal.Title>

      <Modal.Content>
        <form onSubmit={handleSubmit}>
          <Field
            className="mt-2 mb-2"
            label={t("drivers:end_of_shift")}
            name="end_of_shift"
            inputProps={{
              value: driverIdEndOfWork,
              onChange: (e) => setDriverIdEndOfWork(e.currentTarget.value),
              type: "time",
              required: true,
              min: dayjs().format("HH:mm"),
              autoFocus: true,
            }}
          />

          <Field
            label="Powód"
            name="reason"
            className="mt-5"
            inputProps={{
              value: reason,
              required: true,
              onChange: (e) => setReason(e.currentTarget.value),
            }}
          />

          <div className={cls.actions}>
            <Button onClick={handleClose} color="muted" variation="outlined">
              {t("cancel")}
            </Button>

            <Button color="primary" type="submit" raised disabled={isSubmitting}>
              {t("confirm")}
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal>
  )
}
